/**
 * Trade Import Service (KAN-259)
 * Frontend API client for module-agnostic import config CRUD.
 */

import { apiClient, APIResponse } from './client';
import { ValueTransform } from '../../types/tradeImageCapture';

// ========== Types ==========

export interface ColumnMapping {
  sourceColumn: string;
  targetFields: string[];
  description?: string;
}

export interface ConditionalFieldOutput {
  type: 'STATIC' | 'FIELD';
  value?: string;
  transform?: {
    transformType: string;
    config: Record<string, any>;
  };
}

export interface ConditionalFieldCondition {
  field: string;
  operator: string;
  value?: string;
}

export interface ConditionalFieldBranch {
  conditionGroups: { conditions: ConditionalFieldCondition[] }[];
  output: ConditionalFieldOutput;
}

export interface ConditionalFieldEntry {
  targetField: string;
  branches: ConditionalFieldBranch[];
  elseOutput: ConditionalFieldOutput;
}

// ========== Computed Fields (KAN-300) ==========

export type ExtractType = 'VALUE' | 'MONTH' | 'YEAR_2D' | 'YEAR_4D' | 'DAY' | 'FIRST' | 'LAST';

export interface ComputedFieldPart {
  source: string;
  extract: ExtractType;
  length?: number;
  offset?: number;
  transform?: {
    transformType: string;
    config: Record<string, any>;
  };
}

export interface ComputedFieldEntry {
  targetField: string;
  parts: ComputedFieldPart[];
  separator?: string;
}

export interface TradeImportConfig {
  id?: string;
  name: string;
  module: string;
  importContext?: 'Subscription' | 'Expiry';  // default "Subscription"
  lookupField?: string;  // "LegacyReference1" | "LegacyReference2" — expiry configs only
  sourceType: 'CSV' | 'EXCEL' | 'SCREENSHOT' | 'API';
  enabled: boolean;
  detectionHints: string[];
  startRow?: number;          // 1-based, default 1
  startColumn?: string;       // Excel only, e.g. "A", "C"
  includesHeaders?: boolean;  // default true
  columnMappings: ColumnMapping[];
  valueTransforms: ValueTransform[];
  conditionalFields?: ConditionalFieldEntry[];
  computedFields?: ComputedFieldEntry[];
  skipRules?: { conditions: ConditionalFieldCondition[] }[];
  defaultValues: Record<string, string>;
  deferredFields?: string[];
  sampleHeaders?: string[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ImportedTradeRow {
  rowIndex: number;
  fields: Record<string, any>;
  errors: string[];
  warnings: string[];
}

export interface TradeImportResult {
  mode: 'preview' | 'commit';
  totalRows: number;
  validRows: number;
  errorRows: number;
  warningRows: number;
  skippedRows: number;
  trades: ImportedTradeRow[];
  tradesCreated: number;
  errors: string[];
}

// ========== Expiry Import Types ==========

export interface ExpiryMatchResult {
  tradeId: string;
  tradeNumber: string;
  underlyingName: string;
  direction: string;
  lots: number;
  price: number;
  maturityDate: string;
  counterpartyName: string;
  workflowState: string;
}

export interface ExpiryImportedTradeRow {
  rowIndex: number;
  fields: Record<string, any>;
  matchStatus: 'matched' | 'not_found' | 'already_expired';
  matchedTrade: ExpiryMatchResult | null;
  errors: string[];
}

export interface ExpiryImportResult {
  mode: 'preview' | 'commit';
  totalRows: number;
  matchedRows: number;
  notFoundRows: number;
  alreadyExpiredRows: number;
  errorRows: number;
  rows: ExpiryImportedTradeRow[];
  tradesUpdated: number;
}

// ========== Staged Import Types ==========

export interface StagedImportRow {
  rowIndex: number;
  fields: Record<string, any>;
  warnings: string[];
}

export interface StagedImport {
  id?: string;
  status: 'pending' | 'committed';
  module: string;
  label: string;
  importContext: 'Subscription' | 'Expiry';
  configId: string;
  configName: string;
  fileName: string;
  deferredFields: string[];
  totalRows: number;
  enrichedRows: number;
  skippedRows: number;
  rows?: StagedImportRow[];  // only present on get, not on list
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface StagedImportCommitResult {
  tradesCreated: number;
  stagedImportId: string;
  status: string;
}

// ========== Service ==========

class TradeImportService {
  private basePath(clientId: string, module: string): string {
    return `/api/v1/trade-import/${clientId}/${module}/configs`;
  }

  /**
   * List all import configs for a client + module.
   * @param importContext Optional filter: "Subscription" or "Expiry"
   */
  async listConfigs(
    clientId: string,
    module: string,
    importContext?: 'Subscription' | 'Expiry'
  ): Promise<TradeImportConfig[]> {
    const url = importContext
      ? `${this.basePath(clientId, module)}?importContext=${encodeURIComponent(importContext)}`
      : this.basePath(clientId, module);
    const response: APIResponse<TradeImportConfig[]> = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get a specific import config by ID.
   */
  async getConfig(clientId: string, module: string, configId: string): Promise<TradeImportConfig | null> {
    try {
      const response: APIResponse<TradeImportConfig> = await apiClient.get(
        `${this.basePath(clientId, module)}/${configId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Create a new import config.
   */
  async createConfig(clientId: string, module: string, config: TradeImportConfig): Promise<TradeImportConfig> {
    const response: APIResponse<TradeImportConfig> = await apiClient.post(
      this.basePath(clientId, module),
      config
    );
    return response.data;
  }

  /**
   * Update an existing import config.
   */
  async updateConfig(
    clientId: string,
    module: string,
    configId: string,
    config: TradeImportConfig
  ): Promise<TradeImportConfig> {
    const response: APIResponse<TradeImportConfig> = await apiClient.put(
      `${this.basePath(clientId, module)}/${configId}`,
      config
    );
    return response.data;
  }

  /**
   * Delete an import config.
   */
  async deleteConfig(clientId: string, module: string, configId: string): Promise<void> {
    await apiClient.delete(`${this.basePath(clientId, module)}/${configId}`);
  }

  /**
   * Import expiry/settlement data using an expiry config (preview or commit mode).
   */
  async importExpiry(
    clientId: string,
    module: string,
    payload: {
      configId: string;
      fileBase64: string;
      fileName: string;
      mode: 'preview' | 'commit';
    }
  ): Promise<ExpiryImportResult> {
    const response: APIResponse<ExpiryImportResult> = await apiClient.post(
      `/api/v1/trade-import/${clientId}/${module}/import-expiry`,
      payload
    );
    return response.data;
  }

  /**
   * Import trades using a config (preview or commit mode).
   */
  async importTrades(
    clientId: string,
    module: string,
    payload: {
      configId: string;
      fileBase64: string;
      fileName: string;
      mode: 'preview' | 'commit';
    }
  ): Promise<TradeImportResult> {
    const response: APIResponse<TradeImportResult> = await apiClient.post(
      `/api/v1/trade-import/${clientId}/${module}/import-trades`,
      payload
    );
    return response.data;
  }

  // ========== Staged Imports ==========

  private stagedPath(clientId: string, module: string): string {
    return `/api/v1/trade-import/${clientId}/${module}/staged-imports`;
  }

  /**
   * List all staged imports for a client + module (metadata, no rows).
   */
  async listStagedImports(clientId: string, module: string): Promise<StagedImport[]> {
    const response: APIResponse<StagedImport[]> = await apiClient.get(
      this.stagedPath(clientId, module)
    );
    return response.data;
  }

  /**
   * Create a staged import from preview data.
   * @param data.label Optional — backend generates "{configName} — DD/MM/YYYY HH:MM" if absent.
   * @param data.importContext "Subscription" | "Expiry" — defaults to "Subscription".
   */
  async createStagedImport(
    clientId: string,
    module: string,
    data: {
      label?: string;
      importContext?: 'Subscription' | 'Expiry';
      configId: string;
      configName: string;
      fileName: string;
      deferredFields: string[];
      rows: StagedImportRow[];
      skippedRows: number;
    }
  ): Promise<StagedImport> {
    const response: APIResponse<StagedImport> = await apiClient.post(
      this.stagedPath(clientId, module),
      data
    );
    return response.data;
  }

  /**
   * Get a single staged import by ID (includes rows).
   */
  async getStagedImport(
    clientId: string,
    module: string,
    id: string
  ): Promise<StagedImport | null> {
    try {
      const response: APIResponse<StagedImport> = await apiClient.get(
        `${this.stagedPath(clientId, module)}/${id}`
      );
      return response.data;
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  /**
   * Save Draft — update row field values for a staged import.
   */
  async saveStagedImportDraft(
    clientId: string,
    module: string,
    id: string,
    rows: StagedImportRow[]
  ): Promise<StagedImport> {
    const response: APIResponse<StagedImport> = await apiClient.patch(
      `${this.stagedPath(clientId, module)}/${id}`,
      { rows }
    );
    return response.data;
  }

  /**
   * Rename a staged import — update label only (pending imports only).
   */
  async renameStagedImport(
    clientId: string,
    module: string,
    id: string,
    label: string
  ): Promise<StagedImport> {
    const response: APIResponse<StagedImport> = await apiClient.patch(
      `${this.stagedPath(clientId, module)}/${id}`,
      { label }
    );
    return response.data;
  }

  /**
   * Delete a staged import.
   */
  async deleteStagedImport(
    clientId: string,
    module: string,
    id: string
  ): Promise<void> {
    await apiClient.delete(`${this.stagedPath(clientId, module)}/${id}`);
  }

  /**
   * Commit a staged import — writes trades to Firestore.
   */
  async commitStagedImport(
    clientId: string,
    module: string,
    id: string
  ): Promise<StagedImportCommitResult> {
    const response: APIResponse<StagedImportCommitResult> = await apiClient.post(
      `${this.stagedPath(clientId, module)}/${id}/commit`,
      {}
    );
    return response.data;
  }
}

export const tradeImportService = new TradeImportService();
